import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DataService } from "./data.service";
import { MedicalServiceSearchItem } from "../model/medical-service-search-item";
import { PatientCareTrafficLight } from "../model/patient/patient-care-traffic-light";
import { ClinicHistoryItem } from "../model/clinic-history-item";
import { MedicalAppointmentCareEditInfo } from "../model/medical-appointment-care-edit-info";
import { MedicalAppointmentCareUpdate } from "../model/medical-appointment-care-update";
import {
  MedicalConsultationHistory,
  MedicalConsultationHistoryEditInfo,
} from "../model/medical-consultation-history";
import { StudyReportLockInfo } from "../model/study-report-lock-info";
import { ReportViewer } from "../model/report-viewer";
import { StatusPhotography } from "../model/status-photography";
import { StudyRegionFilter } from "../model/study-report/study-region-filter";
import { AuthService } from "./auth.service";

@Injectable()
export class MedicalServiceService {
  constructor(private http: HttpClient, private dataService: DataService, private authService: AuthService) {}

  private readonly medicalAppointmentCareRollBackEndPoint: string =
    "/medicalService/medicalAppointmentCareRollBack/";
  private readonly getIsReAppointmentCheckEndPoint: string =
    "/medicalService/getIsReAppointmentCheck/";
  private readonly getMedicalServiceSearchItemEndPoint: string =
    "/medicalService/getMedicalServiceSearchItem/";
  private readonly getPatientCareTrafficLighsEndPoint: string =
    "/medicalService/getPatientCareTrafficLighs/";
  private readonly getClinicHistoryItemsEndPoint: string =
    "/medicalService/getClinicHistoryItems/";
  private readonly getGetMedicalConsultationHistoryEndPoint: string =
    "/medicalService/getMedicalConsultationHistory/";
  private readonly getMedicalConsultationHistoryEditInfoEndPoint: string =
    "/medicalService/getMedicalConsultationHistoryEditInfo/";
  private readonly getMedicalAppointmentCareEditInfoEndPoint: string =
    "/medicalService/getMedicalAppointmentCareEditInfo/";
  private readonly medicalAppointmentPatientCareTrafficLightUpdateEndPoint: string =
    "/medicalService/medicalAppointmentPatientCareTrafficLightUpdate/";
  private readonly medicalAppointmentCareUpdateEndPoint: string =
    "/medicalService/medicalAppointmentCareUpdate/";
  private readonly medicalAppointmentUnCheckReappointmentEndPoint: string =
    "/medicalService/medicalAppointmentUnCheckReappointment/";
  private readonly medicalAppointmentCheckReappointmentEndPoint: string =
    "/medicalService/medicalAppointmentCheckReappointment/";
  private readonly medicalAppointmentCareStartEndPoint: string =
    "/medicalService/medicalAppointmentCareStart/";
  private readonly medicalAppointmentCareEndEndPoint: string =
    "/medicalService/medicalAppointmentCareEnd/";
  private readonly patientCallEndPoint: string = "/medicalService/patientCall/";
  private readonly patientCallValidationWorkStationEndPoint: string =
    "/medicalService/patientCallValidationWorkStation/";
  private readonly getCommentPreStudyReportEndPoint: string =
    "/medicalService/getCommentPreStudyReport/";
  private readonly getClinicalDataEndPoint: string =
    "/medicalService/getClinicalData/";
  private readonly getStatusPhotographyEndPoint: string =
    "/medicalService/getStatusPhotography/";
  private readonly statusPhotographyUpdateEndPoint: string =
    "/medicalService/statusPhotographyUpdate/";
  private readonly getStudyRegionFilterEndPoint: string =
    "/medicalService/getStudyRegionFilter/";
  private readonly getMedicalServiceSearchItemByMedicalAppointmentIdEndPoint: string =
    "/medicalService/getMedicalServiceSearchItemByMedicalAppointmentId/";

  async getStudyRegionFilter(): Promise<StudyRegionFilter[]> {
    return await this.dataService
      .get<StudyRegionFilter[]>(this.getStudyRegionFilterEndPoint, {})
      .toPromise();
  }

  async getStatusPhotography(): Promise<StatusPhotography[]> {
    return await this.dataService
      .get<StatusPhotography[]>(this.getStatusPhotographyEndPoint, {})
      .toPromise();
  }

  async getMedicalServiceSearchItemByMedicalAppointmentId(
    medicalAppointmentId: number
  ): Promise<MedicalServiceSearchItem> {
    const param = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<MedicalServiceSearchItem>(
        this.getMedicalServiceSearchItemByMedicalAppointmentIdEndPoint,
        param
      )
      .toPromise();
  }

  async patientCall(
    medicalAppointmentId: number,
    workstation: string = ""
  ): Promise<boolean> {
    const param = {
      MedicalAppointmentId: medicalAppointmentId,
      Workstation: workstation,
    };
    return await this.dataService
      .post<boolean>(this.patientCallEndPoint, param)
      .toPromise();
  }

  async patientCallValidationWorkStation(
    medicalAppointmentId: number,
    workstation: string = ""
  ): Promise<boolean> {
    const param = {
      MedicalAppointmentId: medicalAppointmentId,
      Workstation: workstation,
    };
    return await this.dataService
      .post<boolean>(this.patientCallValidationWorkStationEndPoint, param)
      .toPromise();
  }

  async getMedicalConsultationHistoryEditInfo(
    medicalAppointmentId: number
  ): Promise<MedicalConsultationHistoryEditInfo> {
    const param = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<MedicalConsultationHistoryEditInfo>(
        this.getMedicalConsultationHistoryEditInfoEndPoint,
        param
      )
      .toPromise();
  }

  async getMedicalConsultationHistory(
    patientId: number
  ): Promise<MedicalConsultationHistory[]> {
    const param = { patientId: patientId };
    return await this.dataService
      .get<MedicalConsultationHistory[]>(
        this.getGetMedicalConsultationHistoryEndPoint,
        param
      )
      .toPromise();
  }

  async isReAppointmentCheck(medicalAppointmentId: number): Promise<boolean> {
    const param = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<boolean>(this.getIsReAppointmentCheckEndPoint, param)
      .toPromise();
  }

  async statusPhotographyUpdate(
    medicalAppointmentId: number,
    statusPhotographyId: number
  ): Promise<boolean> {
    const param = {
      MedicalAppointmentId: medicalAppointmentId,
      StatusPhotographyId: statusPhotographyId,
    };
    return await this.dataService
      .post<boolean>(this.statusPhotographyUpdateEndPoint, param)
      .toPromise();
  }

  async medicalAppointmentCheckReappointment(
    medicalAppointmentId: number,
    reappointmentDuration: number
  ): Promise<boolean> {
    const param = {
      MedicalAppointmentId: medicalAppointmentId,
      ReappointmentDuration: reappointmentDuration,
    };
    return await this.dataService
      .post<boolean>(this.medicalAppointmentCheckReappointmentEndPoint, param)
      .toPromise();
  }

  async medicalAppointmentUnCheckReappointment(
    medicalAppointmentId: number
  ): Promise<boolean> {
    const param = { MedicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .post<boolean>(this.medicalAppointmentUnCheckReappointmentEndPoint, param)
      .toPromise();
  }

  async medicalAppointmentCareUpdate(
    medicalAppointmentCareUpdate: MedicalAppointmentCareUpdate
  ): Promise<StudyReportLockInfo> {
    return await this.dataService
      .post<StudyReportLockInfo>(
        this.medicalAppointmentCareUpdateEndPoint,
        medicalAppointmentCareUpdate
      )
      .toPromise();
  }

  async medicalAppointmentCareStart(
    medicalServiceSearchItem: MedicalServiceSearchItem
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.medicalAppointmentCareStartEndPoint,
        medicalServiceSearchItem
      )
      .toPromise();
  }

  async medicalAppointmentCareEnd(
    medicalAppointmentId: number,
    physicianId: number,
    esExtraccionista: boolean = false
  ): Promise<boolean> {
    const request = {
      MedicalAppointmentId: medicalAppointmentId,
      PhysicianId: physicianId,
      EsExtraccionista: esExtraccionista,
      CenterId: this.authService.getCenterId()
    };

    return await this.dataService
      .post<boolean>(this.medicalAppointmentCareEndEndPoint, request)
      .toPromise();
  }

  async medicalAppointmentPatientCareTrafficLightUpdate(
    medicalServiceSearchItem: MedicalServiceSearchItem
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.medicalAppointmentPatientCareTrafficLightUpdateEndPoint,
        medicalServiceSearchItem
      )
      .toPromise();
  }

  async getMedicalAppointmentCareEditInfo(
    medicalAppointmentId: number,
    getAtentionAppointmentProcedures: boolean = false
  ): Promise<MedicalAppointmentCareEditInfo> {
    const params = { medicalAppointmentId, getAtentionAppointmentProcedures };
    return await this.dataService
      .get<MedicalAppointmentCareEditInfo>(
        this.getMedicalAppointmentCareEditInfoEndPoint,
        params
      )
      .toPromise();
  }

  async getCommentPreStudyReport(medicalAppointmentId): Promise<string> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<string>(this.getCommentPreStudyReportEndPoint, params)
      .toPromise();
  }

  async getClinicalData(medicalAppointmentId): Promise<string> {
    const params = { medicalAppointmentId: medicalAppointmentId };
    return await this.dataService
      .get<string>(this.getClinicalDataEndPoint, params)
      .toPromise();
  }

  async getPatientCareTrafficLighs(): Promise<PatientCareTrafficLight[]> {
    const params = {};
    return await this.dataService
      .get<PatientCareTrafficLight[]>(
        this.getPatientCareTrafficLighsEndPoint,
        params
      )
      .toPromise();
  }

  async medicalAppointmentCareRollBack(
    medicalServiceSearchItem: MedicalServiceSearchItem
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.medicalAppointmentCareRollBackEndPoint,
        medicalServiceSearchItem
      )
      .toPromise();
  }

  async getClinicHistoryItems(patientId: number): Promise<ClinicHistoryItem[]> {
    const params = { patientId: patientId };
    return await this.dataService
      .get<ClinicHistoryItem[]>(this.getClinicHistoryItemsEndPoint, params)
      .toPromise();
  }

  async getMedicalServiceSearchItem(
    viewAttended: boolean,
    viewMyStudies: boolean,
    equipmentIds?: number[],
    physicianId?: number,
    date?: Date,
    patientId?: number,
    centerId?: number
  ): Promise<MedicalServiceSearchItem[]> {
    let equipmentIdsString = "";
    if (equipmentIds) {
      equipmentIdsString = JSON.stringify(equipmentIds);
    }

    const params = {
      viewAttended: viewAttended != null ? viewAttended : false,
      viewMyStudies: viewMyStudies != null ? viewMyStudies : false,
      equipmentIds: equipmentIdsString,
      physicianId: physicianId,
      date:
        date != null
          ? new Date(
              date.getFullYear(),
              date.getMonth(),
              date.getDate()
            ).toDateString()
          : null,
      patientId: patientId,
      centerId: centerId,
    };

    return await this.dataService
      .get<MedicalServiceSearchItem[]>(
        this.getMedicalServiceSearchItemEndPoint,
        params
      )
      .toPromise();
  }
}
